import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { firstValueFrom, map, startWith } from 'rxjs';

@Component({
  selector: 'ws-new-note-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  template: `
    <vs-dialog
      [config]="{
        disableBackdropDismiss: submitting,
        contentContainerClass: '!overflow-visible',
        dialogContainerClass: 'w-[80vw] max-w-lg !overflow-visible',
      }"
    >
      <vs-dialog-header
        [config]="{
          mode: 'simple',
          showDivider: true,
          hideCloseButton: submitting,

          icon: 'file-plus-02',
          iconTheme: 'primary',

          title: 'Add a note',
          subtitle:
            'Add a new note to this distributor. The note will be visible to all staff members with view access.',
        }"
      ></vs-dialog-header>

      <form class="mt-4" [formGroup]="form" (ngSubmit)="open()">
        <vs-form-field class="mb-3" [config]="{ label: 'Title (optional)' }">
          <input vsInput type="text" placeholder="Note title" formControlName="title" />
        </vs-form-field>

        <vs-rich-text-editor
          formControlName="content"
          placeholder="Note content"
          class="[&_.ck-content]:!min-h-[200px]"
          [allowMediaForm]="true"
        ></vs-rich-text-editor>

        <div class="mt-4 flex flex-wrap gap-2 items-center">
          <button vs-button type="button" class="flex-1" appearance="outline" [disabled]="submitting" (click)="close()">
            Cancel
          </button>

          <button
            *vsLet="valid$ | async as valid"
            vs-button
            type="button"
            class="flex-1"
            theme="primary"
            [disabled]="submitting || !valid"
            (click)="submit()"
          >
            Save
          </button>
        </div>
      </form>
    </vs-dialog>
  `,
})
export class NewNoteDialogComponent {
  private http = inject(HttpClient);
  private toasts = inject(ToastService);
  private formBuilder = inject(FormBuilder);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() distributorId: string | null = null;

  @Output() readonly created = new EventEmitter<string>(true);

  @ViewChild(DialogComponent) private _dialog?: DialogComponent;

  protected submitting = false;

  protected form = this.formBuilder.group({
    title: [null as string | null],
    content: [null as string | null, [Validators.required, Validators.minLength(1)]],
  });

  protected valid$ = this.form.valueChanges.pipe(
    startWith(this.form.value),
    map(() => this.form.valid)
  );

  open() {
    if (!this.distributorId) {
      console.error('[NewNoteDialogComponent] Missing required input property: distributorId');
      return;
    }

    this.reset();

    this._dialog?.open();
    this.changeDetector.markForCheck();
  }

  close() {
    this._dialog?.close();
  }

  protected async submit() {
    this.submitting = true;

    try {
      this.form.disable();
      const { title, content } = this.form.getRawValue();

      const { id } = await firstValueFrom(
        this.http.post<{ id: string }>(`/api/distributors/${this.distributorId}/notes`, { title, content })
      );

      this.created.emit(id);
      this.close();

      this.toasts.show({ type: 'success', title: 'Note Added', text: 'Note added and linked to the distributor.' });
    } catch (err) {
      console.error(err);
      this.toasts.show({
        type: 'error',
        title: 'Unable to add new note',
        text: 'Unable to add a new note and link it to the distributor. Please try again.',
      });
    } finally {
      this.form.enable();
      this.submitting = false;
    }
  }

  protected reset() {
    this.form.reset({ title: null, content: null });
    this.changeDetector.markForCheck();
  }
}
